import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Square } from 'lucide-react';
import { toast } from 'sonner';
import { PromptInputButton } from '@/components/ai-elements/prompt-input';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { startRecording, transcribe, type Recording } from '@/lib/voiceInput';
import { cn } from '@/lib/utils';
import { playMicSound } from '@/lib/micSound';

export const FEATURE_META: FeatureMeta = {
  id: 'ask-cannafax-voice-input',
  label: 'Speak your question',
  description: 'Tap the microphone in the Ask Cannafax chat box, speak, then tap stop; your words appear in the box for you to review before sending',
  triggerType: 'action',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

/** The one voice control for every text box (chat, search, future inputs).
 * Mic → speak → stop → transcript lands in the box. Never sends by itself. */
export function VoiceInputButton({ baseText, onText, disabled, onRecordingChange, onTranscribingChange, subject = 'question' }: {
  /** What the user is speaking — drives the button label and error copy. */
  subject?: 'question' | 'search' | 'message';
  baseText: string; onText: (text: string) => void; disabled?: boolean;
  /** Lets the parent show a live "Listening…" indicator; receives the loudness meter while recording. */
  onRecordingChange?: (recording: boolean, meter: (() => number) | null) => void;
  /** Lets the parent show a "Transcribing…" indicator after recording stops. */
  onTranscribingChange?: (transcribing: boolean) => void;
}) {
  useFeatureRegistration(FEATURE_META);
  const [state, setState] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const rec = useRef<Recording | null>(null);
  const abort = useRef<AbortController | null>(null);
  const notify = useRef(onRecordingChange);
  notify.current = onRecordingChange;
  const notifyTranscribing = useRef(onTranscribingChange);
  notifyTranscribing.current = onTranscribingChange;

  useEffect(() => () => {
    rec.current?.cancel(); abort.current?.abort();
    notify.current?.(false, null);
    notifyTranscribing.current?.(false);
  }, []);

  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return null;

  const start = async () => {
    try {
      rec.current = await startRecording();
      playMicSound(true);
      setState('recording');
      const r = rec.current;
      notify.current?.(true, () => r.level());
    }
    catch { toast.error('Microphone unavailable', { description: `Allow microphone access in your browser to speak your ${subject}.` }); }
  };

  const stop = async () => {
    const r = rec.current; rec.current = null;
    notify.current?.(false, null);
    if (!r) return;
    playMicSound(false);
    setState('transcribing');
    notifyTranscribing.current?.(true);
    const prefix = baseText.trim() ? `${baseText.trim()} ` : '';
    try {
      const file = await r.stop();
      abort.current = new AbortController();
      await transcribe(file, (t) => onText(prefix + t), abort.current.signal);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        toast.error("Couldn't hear that", { description: e instanceof Error ? e.message : 'Please try again.' });
      }
    } finally { abort.current = null; notifyTranscribing.current?.(false); setState('idle'); }
  };

  const label = state === 'recording' ? 'Stop and transcribe' : state === 'transcribing' ? 'Transcribing' : `Speak your ${subject}`;
  return (
    <PromptInputButton
      type="button"
      size="icon-sm"
      aria-label={label}
      title={label}
      aria-pressed={state === 'recording'}
      disabled={disabled || state === 'transcribing'}
      onClick={() => void (state === 'recording' ? stop() : start())}
      className={cn('circle-8', state === 'recording' && 'text-destructive animate-pulse')}
    >
      {state === 'transcribing' ? <Loader2 className="h-4 w-4 animate-spin" /> : state === 'recording' ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-5 w-5" />}
    </PromptInputButton>
  );
}
